import { css, styled } from 'styled-components';

import {
  BORDER_WIDTH,
  getSegmentedChipsButtonBorderRadius,
  iconSizeByButtonSize,
  outlineWidth,
} from '@/components/chips/chips.constants.ts';
import type {
  ChipsSize,
  ChipVariant,
  SegmentedChipsRadiusRules,
} from '@/components/chips/chips.types.ts';
import { stateBlock } from '@/components/chips/chipsButton/functions.ts';
import { colors, responsive } from '@/utils';

type StyledButtonProps = {
  $size: ChipsSize;
  $selected?: boolean;
  disabled?: boolean;
  /** 'group' = transparent unselected (for use inside SegmentedChips pill). Default 'standalone'. */
  $variant?: ChipVariant;
  $radiusRules?: SegmentedChipsRadiusRules;
};

const sizeStyles: Record<ChipsSize, { gap: string; padding: string }> = {
  xSmall: {
    gap: `calc(${responsive.spacing.xsmall} - 0.125rem)`,
    padding: `calc(${responsive.spacing.xsmall} - 0.125rem) ${responsive.spacing['xlarge']}`,
  },
  small: {
    gap: responsive.spacing.xsmall,
    padding: `${responsive.spacing.xsmall} ${responsive.spacing['2xlarge']}`,
  },
  medium: {
    gap: responsive.spacing.medium,
    padding: `${responsive.spacing.medium} ${responsive.spacing['3xlarge']}`,
  },
};

const action = colors.background.action.soft;
const actionStroke = colors.stroke.action.soft;
const actionText = colors.text.action.on.soft;

//************************************
// Mixins for state blocks
//************************************

const selectedStyles = css`
  ${stateBlock({ bg: action[300], border: actionStroke[300], color: actionText[300] })}
  &:hover {
    ${stateBlock({ bg: action[200], border: actionStroke[200], color: actionText[200] })}
  }
  &:active {
    ${stateBlock({ bg: action[100], border: actionStroke[100], color: actionText[100] })}
  }
  &:focus-visible {
    outline: ${outlineWidth} solid ${actionStroke[300]};
    ${stateBlock({ bg: action[300], border: actionStroke[200], color: actionText[300] })}
  }
`;

const standaloneDefaultStyles = css`
  border: ${BORDER_WIDTH} solid ${colors.stroke.neutral[100]};
  ${stateBlock({
    bg: colors.background[100],
    border: colors.stroke.neutral[100],
    color: colors.text.action[100],
  })}
  &:hover {
    ${stateBlock({
      bg: colors.background.neutral.soft[200],
      border: colors.stroke.neutral[200],
      color: colors.text.action[100],
    })}
  }
  &:active {
    ${stateBlock({
      bg: colors.background.neutral.soft[300],
      border: colors.stroke.neutral[200],
      color: colors.text.action[100],
    })}
  }
  &:focus-visible {
    outline-color: ${colors.stroke.focused};
    ${stateBlock({
      bg: colors.background[100],
      border: colors.stroke.neutral[100],
      color: colors.text.neutral[100],
    })}
  }
`;

const groupDefaultStyles = css`
  border: ${BORDER_WIDTH} solid transparent;
  ${stateBlock({
    bg: 'transparent',
    border: 'transparent',
    color: colors.text.neutral[100],
  })}
  &:hover {
    ${stateBlock({
      bg: colors.background.neutral.soft[200],
      border: 'transparent',
      color: colors.text.action[100],
    })}
  }
  &:active {
    ${stateBlock({
      bg: colors.background.neutral.soft[300],
      border: 'transparent',
      color: colors.text.action[100],
    })}
  }
  &:focus-visible {
    outline-color: ${colors.stroke.focused};
    ${stateBlock({
      bg: 'transparent',
      border: 'transparent',
      color: colors.text.neutral[100],
    })}
  }
`;

//************************************
// Styled button
//************************************

export const StyledButton = styled.button<StyledButtonProps>`
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  border-radius: ${({ $size, $variant, $radiusRules }) =>
    getSegmentedChipsButtonBorderRadius({
      size: $size,
      variant: $variant,
      radiusRules: $radiusRules,
    })};
  gap: ${({ $size }) => sizeStyles[$size].gap};
  padding: ${({ $size }) => sizeStyles[$size].padding};
  cursor: pointer;
  outline-offset: ${outlineWidth};
  outline: ${outlineWidth} solid transparent;
  /* Size to content (icon + label); avoid flex shrink so label is not truncated */
  flex-shrink: 0;
  white-space: nowrap;
  /* Size headIcon (and other) SVGs to match chip – prevents tiny icons from flex shrink */
  svg {
    flex-shrink: 0;
    width: ${({ $size }) => iconSizeByButtonSize[$size]};
    height: ${({ $size }) => iconSizeByButtonSize[$size]};
  }
  ${({ $variant }) => ($variant === 'group' ? groupDefaultStyles : standaloneDefaultStyles)}

  ${({ $selected }) => $selected && selectedStyles}

  &:disabled {
    ${stateBlock({
      bg: colors.background.neutral.soft[200],
      border: colors.stroke.neutral[50],
      color: colors.text.neutral.on.soft[200],
    })}
    cursor: not-allowed;
    outline: none;
    &:hover,
    &:active,
    &:focus-visible {
      ${stateBlock({
        bg: colors.background.neutral.soft[200],
        border: colors.stroke.neutral[50],
        color: colors.text.neutral.on.soft[200],
      })}
    }
  }

  &[data-error='true'] {
    ${stateBlock({
      bg: colors.background.critical.soft[100],
      border: colors.stroke.critical.soft[100],
      color: colors.text.critical.on.soft[100],
    })}
    cursor: not-allowed;
    outline: none;
    &:hover,
    &:active,
    &:focus-visible {
      ${stateBlock({
        bg: colors.background.critical.soft[100],
        border: colors.stroke.critical.soft[100],
        color: colors.text.critical.on.soft[200],
      })}
    }
  }
`;
